import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, DollarSign } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/referrals")({
  head: () => ({ meta: [{ title: "Referral Tracker" }, { name: "robots", content: "noindex" }] }),
  component: ReferralsPage,
});

type R = {
  id?: string;
  referrer_name: string; referrer_email: string | null; referrer_phone: string | null;
  referred_name: string; referred_email: string | null; referred_phone: string | null; referred_address: string | null;
  status: string;
  reward_amount: number;
  reward_paid: boolean;
  reward_paid_on: string | null;
  referred_on: string;
  closed_on: string | null;
  notes: string | null;
};

const today = () => new Date().toISOString().slice(0, 10);
const empty = (): R => ({
  referrer_name: "", referrer_email: "", referrer_phone: "",
  referred_name: "", referred_email: "", referred_phone: "", referred_address: "",
  status: "new", reward_amount: 100, reward_paid: false, reward_paid_on: null,
  referred_on: today(), closed_on: null, notes: "",
});

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-500/15 text-blue-600",
  contacted: "bg-amber-500/15 text-amber-600",
  quoted: "bg-purple-500/15 text-purple-600",
  won: "bg-emerald-500/15 text-emerald-600",
  lost: "bg-muted text-muted-foreground",
};

function ReferralsPage() {
  const [list, setList] = useState<R[]>([]);
  const [current, setCurrent] = useState<R | null>(null);

  const load = async () => {
    const { data } = await supabase.from("referrals").select("*").order("referred_on", { ascending: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const won = list.filter(r => r.status === "won").length;
    const pending = list.filter(r => r.reward_amount > 0 && !r.reward_paid && r.status === "won").reduce((s, r) => s + Number(r.reward_amount), 0);
    const paid = list.filter(r => r.reward_paid).reduce((s, r) => s + Number(r.reward_amount), 0);
    return { won, pending, paid };
  }, [list]);

  const save = async () => {
    if (!current || !current.referrer_name || !current.referred_name) return toast.error("Referrer and referred name required");
    if (current.id) {
      const { error } = await supabase.from("referrals").update(current).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("referrals").insert(current as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("referrals").delete().eq("id", id); load(); } };
  const markPaid = async (r: R) => {
    await supabase.from("referrals").update({ reward_paid: true, reward_paid_on: today() }).eq("id", r.id!);
    toast.success("Reward paid"); load();
  };

  if (!current) return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Referrals</h1>
          <p className="text-sm text-muted-foreground mt-1">Track who sent us new business and reward payouts.</p>
        </div>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New</Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-3 text-sm"><span className="text-muted-foreground">Won:</span> <span className="font-semibold">{totals.won}</span></div>
        <div className="rounded-lg border border-border bg-card p-3 text-sm"><span className="text-muted-foreground">Rewards pending:</span> <span className="font-semibold text-amber-600">${totals.pending.toFixed(2)}</span></div>
        <div className="rounded-lg border border-border bg-card p-3 text-sm"><span className="text-muted-foreground">Rewards paid:</span> <span className="font-semibold text-emerald-600">${totals.paid.toFixed(2)}</span></div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(r => (
          <div key={r.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{r.referred_name}</h3>
                <p className="text-xs text-muted-foreground">from {r.referrer_name}</p>
                <p className="text-xs mt-1">
                  <span className={`rounded-full px-2 py-0.5 ${STATUS_COLORS[r.status] || 'bg-muted'}`}>{r.status}</span>
                  {r.reward_amount > 0 && <span className="ml-2 text-muted-foreground">${r.reward_amount} {r.reward_paid ? '✓ paid' : 'unpaid'}</span>}
                </p>
              </div>
              <button onClick={() => del(r.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => setCurrent(r)}>Open</Button>
              {r.status === "won" && !r.reward_paid && r.reward_amount > 0 && (
                <Button size="sm" variant="secondary" onClick={() => markPaid(r)}><DollarSign className="h-4 w-4 mr-1" />Pay</Button>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-muted-foreground text-sm">No referrals yet.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
      </div>
      <h2 className="mt-4 font-display text-lg font-semibold">Referrer</h2>
      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        <div><label className="text-xs text-muted-foreground">Name</label><Input value={current.referrer_name} onChange={e => setCurrent({ ...current, referrer_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Email</label><Input value={current.referrer_email || ""} onChange={e => setCurrent({ ...current, referrer_email: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Phone</label><Input value={current.referrer_phone || ""} onChange={e => setCurrent({ ...current, referrer_phone: e.target.value })} /></div>
      </div>
      <h2 className="mt-6 font-display text-lg font-semibold">Referred</h2>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div><label className="text-xs text-muted-foreground">Name</label><Input value={current.referred_name} onChange={e => setCurrent({ ...current, referred_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Address</label><Input value={current.referred_address || ""} onChange={e => setCurrent({ ...current, referred_address: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Email</label><Input value={current.referred_email || ""} onChange={e => setCurrent({ ...current, referred_email: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Phone</label><Input value={current.referred_phone || ""} onChange={e => setCurrent({ ...current, referred_phone: e.target.value })} /></div>
      </div>
      <h2 className="mt-6 font-display text-lg font-semibold">Status & Reward</h2>
      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        <div><label className="text-xs text-muted-foreground">Status</label>
          <select className="w-full rounded border border-input bg-background p-2" value={current.status} onChange={e => setCurrent({ ...current, status: e.target.value })}>
            <option value="new">New</option><option value="contacted">Contacted</option><option value="quoted">Quoted</option><option value="won">Won</option><option value="lost">Lost</option>
          </select>
        </div>
        <div><label className="text-xs text-muted-foreground">Referred On</label><Input type="date" value={current.referred_on} onChange={e => setCurrent({ ...current, referred_on: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Closed On</label><Input type="date" value={current.closed_on || ""} onChange={e => setCurrent({ ...current, closed_on: e.target.value || null })} /></div>
        <div><label className="text-xs text-muted-foreground">Reward $</label><NumberInput value={current.reward_amount} onValueChange={n => setCurrent({ ...current, reward_amount: n })} /></div>
        <div className="flex items-end gap-2"><label className="text-xs flex items-center gap-2"><input type="checkbox" checked={current.reward_paid} onChange={e => setCurrent({ ...current, reward_paid: e.target.checked, reward_paid_on: e.target.checked ? today() : null })} /> Reward paid</label></div>
        <div><label className="text-xs text-muted-foreground">Paid On</label><Input type="date" value={current.reward_paid_on || ""} onChange={e => setCurrent({ ...current, reward_paid_on: e.target.value || null })} /></div>
      </div>
      <Textarea placeholder="Notes" className="mt-4" rows={4} value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} />
    </div>
  );
}
